import React, { useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import './CustomDateRangePicker.css';

const CustomDateRangePicker = ({ onApply, onCancel, initialStart, initialEnd }) => {
    const [startDateTime, setStartDateTime] = useState(initialStart || '');
    const [endDateTime, setEndDateTime] = useState(initialEnd || '');
    const [activeField, setActiveField] = useState('start'); // 'start' or 'end'
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [viewMode, setViewMode] = useState('days'); // 'days', 'months', 'years'

    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const handleDayClick = (dateStr) => {
        if (activeField === 'start') {
            const time = startDateTime ? startDateTime.split('T')[1] : '00:00';
            setStartDateTime(`${dateStr}T${time}`);
            setActiveField('end');
        } else {
            const time = endDateTime ? endDateTime.split('T')[1] : '23:59';
            setEndDateTime(`${dateStr}T${time}`);
        }
    };

    const handleTimeChange = (e, field) => {
        const val = e.target.value;
        if (field === 'start') {
            if (!startDateTime) {
                const today = new Date().toISOString().split('T')[0];
                setStartDateTime(`${today}T${val}`);
            } else {
                const dateStr = startDateTime.split('T')[0];
                setStartDateTime(`${dateStr}T${val}`);
            }
        } else {
            if (!endDateTime) {
                const today = new Date().toISOString().split('T')[0];
                setEndDateTime(`${today}T${val}`);
            } else {
                const dateStr = endDateTime.split('T')[0];
                setEndDateTime(`${dateStr}T${val}`);
            }
        }
    };

    const handlePrev = () => {
        if (viewMode === 'days') setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
        else if (viewMode === 'months') setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
        else if (viewMode === 'years') setCurrentMonth(new Date(currentMonth.getFullYear() - 12, currentMonth.getMonth(), 1));
    };

    const handleNext = () => {
        if (viewMode === 'days') setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
        else if (viewMode === 'months') setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));
        else if (viewMode === 'years') setCurrentMonth(new Date(currentMonth.getFullYear() + 12, currentMonth.getMonth(), 1));
    };

    const renderMonths = () => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return (
            <div className="drp-month-grid">
                {months.map((m, i) => (
                    <div key={m} className={`drp-month-item ${currentMonth.getMonth() === i ? 'selected' : ''}`} onClick={() => {
                        setCurrentMonth(new Date(currentMonth.getFullYear(), i, 1));
                        setViewMode('days');
                    }}>
                        {m}
                    </div>
                ))}
            </div>
        );
    };

    const renderYears = () => {
        const currentY = currentMonth.getFullYear();
        const startYear = Math.floor(currentY / 12) * 12; 
        const years = [];
        for (let i = 0; i < 12; i++) {
            const y = startYear + i;
            years.push(
                <div key={y} className={`drp-year-item ${currentY === y ? 'selected' : ''}`} onClick={() => {
                    setCurrentMonth(new Date(y, currentMonth.getMonth(), 1));
                    setViewMode('months'); // Usually after picking year, you pick month
                }}>
                    {y}
                </div>
            );
        }
        return (
            <div className="drp-year-grid">
                {years}
            </div>
        );
    };

    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const totalDays = daysInMonth(year, month);
        const startDay = firstDayOfMonth(year, month);

        const days = [];
        for (let i = 0; i < startDay; i++) {
            days.push(<div key={`empty-${i}`} className="drp-day empty"></div>);
        }
        for (let i = 1; i <= totalDays; i++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            const isStart = startDateTime && startDateTime.startsWith(dateStr);
            const isEnd = endDateTime && endDateTime.startsWith(dateStr);
            
            let inRange = false;
            if (startDateTime && endDateTime && !isStart && !isEnd) {
                const d = new Date(year, month, i).getTime();
                const s = new Date(startDateTime.split('T')[0]).getTime();
                const e = new Date(endDateTime.split('T')[0]).getTime();
                if (d > s && d < e) inRange = true;
            }

            let cName = "drp-day";
            if (isStart) cName += " start-date";
            if (isEnd) cName += " end-date";
            if (inRange) cName += " in-range";

            days.push(
                <div key={i} className={cName} onClick={() => handleDayClick(dateStr)}>
                    {i}
                </div>
            );
        }
        return days;
    };

    return (
        <div className="custom-drp-container">
            <div className="drp-fields">
                <div className={`drp-field-wrap ${activeField === 'start' ? 'active' : ''}`} onClick={() => setActiveField('start')}>
                    <label>Start Date & Time</label>
                    <div className="drp-input-box">
                        <CalendarIcon size={14} className="drp-icon"/>
                        <span className="drp-date-display">{startDateTime ? startDateTime.split('T')[0] : 'YYYY-MM-DD'}</span>
                        <input type="time" value={startDateTime ? startDateTime.split('T')[1] : ''} onChange={e => handleTimeChange(e, 'start')} />
                    </div>
                </div>
                <div className={`drp-field-wrap ${activeField === 'end' ? 'active' : ''}`} onClick={() => setActiveField('end')}>
                    <label>End Date & Time</label>
                    <div className="drp-input-box">
                        <CalendarIcon size={14} className="drp-icon"/>
                        <span className="drp-date-display">{endDateTime ? endDateTime.split('T')[0] : 'YYYY-MM-DD'}</span>
                        <input type="time" value={endDateTime ? endDateTime.split('T')[1] : ''} onChange={e => handleTimeChange(e, 'end')} />
                    </div>
                </div>
            </div>

            <div className="drp-calendar">
                <div className="drp-calendar-header">
                    <button className="drp-nav-btn" onClick={handlePrev}>
                        <ChevronLeft size={16}/>
                    </button>
                    <div className="drp-month-label">
                        <span className="drp-clickable-label" onClick={() => setViewMode(viewMode === 'months' ? 'days' : 'months')}>
                            {viewMode === 'years' ? `${Math.floor(currentMonth.getFullYear() / 12) * 12} - ${Math.floor(currentMonth.getFullYear() / 12) * 12 + 11}` : currentMonth.toLocaleString('default', { month: 'short' })}
                        </span>
                        {viewMode !== 'years' && ' '}
                        {viewMode !== 'years' && (
                            <span className="drp-clickable-label" onClick={() => setViewMode(viewMode === 'years' ? 'days' : 'years')}>
                                {currentMonth.getFullYear()}
                            </span>
                        )}
                    </div>
                    <button className="drp-nav-btn" onClick={handleNext}>
                        <ChevronRight size={16}/>
                    </button>
                </div>
                {viewMode === 'days' && (
                    <div className="drp-calendar-grid">
                        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="drp-day-name">{d}</div>)}
                        {renderCalendar()}
                    </div>
                )}
                {viewMode === 'months' && renderMonths()}
                {viewMode === 'years' && renderYears()}
            </div>

            <div className="drp-actions">
                <button className="drp-btn cancel" onClick={onCancel}>Cancel</button>
                <button className="drp-btn apply" onClick={() => onApply(startDateTime, endDateTime)} disabled={!startDateTime || !endDateTime}>Apply</button>
            </div>
        </div>
    );
};

export default CustomDateRangePicker;
